import type { JsonLdObject } from '../structured-data'

// Mirrors react-router's own `escapeHtml` (lib/dom/ssr/markup.ts) — the escaper
// behind its `{ 'script:ld+json': … }` meta descriptor, which is what every
// ROUTE-level graph on this site goes through. Repeated here rather than
// imported because it is internal to that module.
//
// The `<` entry is the one that matters: without it a string containing
// `</script>` would close the tag early and the rest of the JSON would be parsed
// as markup. `&` and `>` are escaped for the same defence-in-depth reason
// react-router does, and U+2028/U+2029 because they are literal line terminators
// in JavaScript source but legal inside a JSON string.
//
// Those two are spelled by char code rather than written as literals: they render
// as nothing in an editor, so a literal pair would be invisible to review and
// trivial to lose to a stray reformat.
const LINE_SEPARATOR = String.fromCharCode(0x2028)
const PARAGRAPH_SEPARATOR = String.fromCharCode(0x2029)

const ESCAPE_LOOKUP: Record<string, string> = {
  '&': '\\u0026',
  '>': '\\u003e',
  '<': '\\u003c',
  [LINE_SEPARATOR]: '\\u2028',
  [PARAGRAPH_SEPARATOR]: '\\u2029',
}

const ESCAPE_REGEX = new RegExp(`[&><${LINE_SEPARATOR}${PARAGRAPH_SEPARATOR}]`, 'g')

/** Exported for the unit test — the escaping is the only behaviour worth pinning. */
export const serializeJsonLd = (data: JsonLdObject): string =>
  JSON.stringify(data).replace(ESCAPE_REGEX, (match) => ESCAPE_LOOKUP[match] ?? match)

/**
 * A `<script type="application/ld+json">` block (VIT-17746).
 *
 * Needed only for the SITE-WIDE graph, which root.tsx renders in `<head>`
 * directly. Route-level graphs must NOT use this — they go through each route's
 * `meta` export as a `{ 'script:ld+json': … }` descriptor instead.
 *
 * The reason the site graph can't do the same: `<Meta />` renders only the
 * deepest matched route's `meta`, so a node declared in root's `meta` export is
 * dropped on every page that has a `meta` of its own — which here is all of them.
 * That is the same trap root.tsx's `meta` comment documents for `<title>`.
 *
 * `script-src` carries `'unsafe-inline'` (see content-security-policy.ts on why
 * nonces are infeasible under edge caching), so this needs no CSP work.
 */
export const StructuredData = ({ data }: { data: JsonLdObject }) => (
  // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD must be raw text inside the script tag; serializeJsonLd escapes every character that could close it
  <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(data) }} />
)
